import React, { useState, useEffect, useCallback, useContext } from 'react';
import { Brain, Volume2, VolumeX, BarChart2, SkipForward, Timer, X, Heart, Zap, Trophy, Wind, Target } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { synthesizeSpeech } from '../lib/openai';
import { AppContext } from '../context/AppContext';

const CommandButton = ({ icon: Icon, label, onClick, variant = 'default' }) => (
  <button
    onClick={onClick}
    className={`glass-button p-3 flex items-center gap-2 ${
      variant === 'danger' ? 'text-red-300' : ''
    }`}
  >
    <Icon className="w-4 h-4" />
    <span className="text-sm">{label}</span>
  </button>
);

const GoalProgress = ({ title, value, target, unit, icon: Icon }) => (
  <div className="glass p-3 rounded-lg space-y-2">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4 text-white/70" />
        <span className="text-sm text-white/70">{title}</span>
      </div>
      <span className="text-sm font-medium">{value}/{target} {unit}</span>
    </div>
    <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
      <motion.div 
        className="h-full bg-gradient-to-r from-blue-400 to-purple-400"
        initial={{ width: 0 }}
        animate={{ width: `${(value / target) * 100}%` }}
        transition={{ duration: 0.5 }}
      />
    </div>
  </div>
);

export const AICoach = () => {
  const [isMuted, setIsMuted] = useState(false);
  const [currentAdvice, setCurrentAdvice] = useState('Great pace! Try to maintain this rhythm for the next 10 minutes to improve your endurance.');
  const { setShowStats, setIsRunning } = useContext(AppContext);
  const [personalizedTips, setPersonalizedTips] = useState([]);
  const [currentGoal, setCurrentGoal] = useState({
    type: 'distance',
    target: 5,
    progress: 2.4,
    nextMilestone: 3
  });

  const speak = useCallback(async (text: string) => {
    if (!isMuted) {
      try {
        const audioData = await synthesizeSpeech(text);
        const audioContext = new AudioContext();
        const audioBuffer = await audioContext.decodeAudioData(audioData);
        const source = audioContext.createBufferSource();
        source.buffer = audioBuffer;
        source.connect(audioContext.destination);
        source.start(0);
      } catch (error) {
        console.error('Error playing audio:', error);
      }
    }
  }, [isMuted]);

  const toggleMute = async () => {
    setIsMuted(!isMuted);
    await speak(isMuted ? 'Audio feedback enabled' : 'Audio feedback muted');
  };

  const handleCommand = async (command: string) => {
    switch (command) {
      case 'show_stats':
        setShowStats(true);
        await speak("Displaying your current running statistics.");
        break;
      case 'next_song':
        await speak("Skipping to the next song that matches your pace.");
        break;
      case 'pace':
        await speak("Your current pace is 4 minutes and 50 seconds per kilometer. You're maintaining a steady rhythm.");
        break;
      case 'end_run':
        await speak("Ending your run now.");
        setIsRunning(false);
        break;
      default:
        break;
    }
  };

  const generatePersonalizedTip = useCallback(() => {
    const tips = [
      {
        type: 'form',
        icon: Wind,
        title: 'Form Check',
        message: "Your cadence is optimal at 172 steps/minute. This is perfect for reducing impact and maintaining efficiency."
      },
      {
        type: 'pacing',
        icon: Timer,
        title: 'Pace Analysis',
        message: "You're maintaining a steady 4:50/km pace. This is ideal for your target heart rate zone."
      },
      {
        type: 'milestone',
        icon: Target,
        title: 'Milestone Alert',
        message: `Just ${(currentGoal.nextMilestone - currentGoal.progress).toFixed(1)}km to your next milestone! Keep pushing!`
      }
    ];

    return tips[Math.floor(Math.random() * tips.length)];
  }, [currentGoal]);

  useEffect(() => {
    const interval = setInterval(async () => {
      const newTip = generatePersonalizedTip();
      setPersonalizedTips(prev => [newTip, ...prev].slice(0, 3));
      await speak(newTip.message);
    }, 45000);

    return () => clearInterval(interval);
  }, [generatePersonalizedTip, speak]);

  const commands = [
    { id: 'show_stats', icon: BarChart2, label: 'Show Stats' },
    { id: 'next_song', icon: SkipForward, label: 'Next Song' },
    { id: 'pace', icon: Timer, label: "What's my pace?" },
    { id: 'end_run', icon: X, label: 'End Run', variant: 'danger' }
  ];

  return (
    <motion.div 
      className="glass p-6 rounded-xl space-y-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Brain className="w-6 h-6" />
          <h2 className="text-xl font-semibold">AI Coach</h2>
        </div>
        <button 
          onClick={toggleMute}
          className="glass-button p-2"
        >
          {isMuted ? (
            <VolumeX className="w-5 h-5" />
          ) : (
            <Volume2 className="w-5 h-5" />
          )}
        </button>
      </div>

      <AnimatePresence>
        {personalizedTips.map((tip, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            className="glass p-4 rounded-lg space-y-2"
          >
            <div className="flex items-center gap-2">
              <tip.icon className="w-4 h-4 text-blue-400" />
              <span className="font-medium">{tip.title}</span>
            </div>
            <p className="text-sm text-white/90">{tip.message}</p>
          </motion.div>
        ))}
      </AnimatePresence>

      <div className="space-y-3">
        <GoalProgress 
          title="Distance Goal" 
          value={currentGoal.progress} 
          target={currentGoal.target} 
          unit="km"
          icon={Target}
        />
        <GoalProgress 
          title="Calories Burned" 
          value={187} 
          target={300} 
          unit="kcal"
          icon={Zap}
        />
        <GoalProgress 
          title="Time in Zone" 
          value={18} 
          target={30} 
          unit="min"
          icon={Heart}
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        {commands.map(({ id, icon, label, variant }) => (
          <CommandButton
            key={id}
            icon={icon}
            label={label}
            onClick={() => handleCommand(id)}
            variant={variant}
          />
        ))}
      </div>

      <div className="glass p-4 rounded-lg space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Trophy className="w-4 h-4 text-yellow-400" />
            <span className="font-medium">Live Achievements</span>
          </div>
          <span className="text-sm text-white/70">2/3 Complete</span>
        </div>
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm">
            <Zap className="w-4 h-4 text-green-400" />
            <span className="text-white/70">Consistent Pace: 5 min streak</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <Heart className="w-4 h-4 text-red-400" />
            <span className="text-white/70">Optimal Zone: 3 min remaining</span>
          </div>
        </div>
      </div>
    </motion.div>
  );
};